import { useMemo } from "react";
import { TOTAL_GROUP_BY, TOTAL_GROUP_KEY } from "@salestrak/widgets";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { comparisonBasis, previousWindow } from "../shared/comparison-window";
import { useWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { UNASSIGNED_DIMENSION_KEY } from "#app/widgets/dashboards/use-dimension-counts";
import type { FieldMeasure } from "#app/widgets/dashboards/widget-measure";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";

/** What a metric card shows. `total` = the count of every project in scope;
 * `unassigned` = the count with no assignee; `field` = an aggregate of a custom
 * field over every project in scope (`fieldMeasures` names the field). */
export type MetricMeasure = "total" | "unassigned" | "field";

// `total` and `field` ask the server's single-bucket token, so an average or an
// extreme is computed over the whole set in one pass — summing per-stage
// averages would not be an average. `unassigned` groups by assignee and reads
// only the `__unassigned__` bucket.
const GROUP_BY: Record<MetricMeasure, string> = {
  total: TOTAL_GROUP_BY,
  unassigned: "assignees.entityId",
  field: TOTAL_GROUP_BY,
};
const BUCKET: Record<MetricMeasure, string> = {
  total: TOTAL_GROUP_KEY,
  unassigned: UNASSIGNED_DIMENSION_KEY,
  field: TOTAL_GROUP_KEY,
};

/** One shared empty ask, so an unmeasured card's query variables never churn. */
const NO_MEASURES: readonly FieldMeasure[] = [];

/** The figure one bucket of a grouped read answers: the field aggregates added
 *  across the fields (null when no project holds any), else the count. */
function bucketValue(
  counts: Record<string, number>,
  values: Record<string, (number | null)[]>,
  bucket: string,
  measured: boolean,
): { value: number | null; count: number } {
  const count = counts[bucket] ?? 0;
  // A bucket the server did not return holds no projects: a count of zero,
  // and nothing to aggregate.
  const figures = (values[bucket] ?? []).filter((figure): figure is number => figure !== null);
  const value = measured
    ? figures.length > 0
      ? figures.reduce((sum, figure) => sum + figure, 0)
      : null
    : count;
  return { value, count };
}

/**
 * The metric card's data layer: folds the ambient dashboard window + the card's
 * own Filters blob into a `where` (via `useScopedWhere`, exactly like
 * `useDimensionSlices`), then reads one bucket off the grouped counts. With
 * field measures (`fieldMeasures` in `widget-measure.ts`) the figure is the
 * bucket's aggregates ADDED across the fields — null when none of its projects
 * hold any of them; a field nobody filled in adds nothing — and the count of
 * projects behind it rides along. A `field` measure still waiting for its
 * fields asks nothing. With `compare`, a second read asks the same figure over
 * the equal-length window before the card's (`previousWindow`) and `previous`
 * carries it with the `basis` its copy names ("last week"); an all-time window
 * has no previous one, so both are null and the second read is skipped.
 */
export function useMetric(
  measure: MetricMeasure,
  filters: unknown,
  dateField: "creationDate" | "lastUpdateDate" = "creationDate",
  fieldMeasures: readonly FieldMeasure[] = NO_MEASURES,
  compare = false,
) {
  const { base, override } = useWidgetFilters(filters);
  const { where, dateRange, scope } = useScopedWhere(base, { dateField, override });
  const measured = fieldMeasures.length > 0;
  const pending = measure === "field" && !measured;
  const { counts, values, ready, error } = useIssueGroupCounts({
    where,
    groupBy: pending ? undefined : GROUP_BY[measure],
    measures: fieldMeasures,
  });

  const windowKey = scope.dateRange.key;
  const comparable = compare && windowKey !== "all-time";
  // The previous window rides in as the card's own override, so every other
  // facet of the fold (member set, Only-Me, the card's Filters) holds still.
  const previousOverride = useMemo(
    () => ({ ...override, dateRange: { key: "custom" as const, custom: previousWindow(dateRange) } }),
    [override, dateRange],
  );
  const { where: previousWhere } = useScopedWhere(base, { dateField, override: previousOverride });
  const previousCounts = useIssueGroupCounts({
    where: previousWhere,
    groupBy: pending || !comparable ? undefined : GROUP_BY[measure],
    measures: fieldMeasures,
  });

  const { value, count } = bucketValue(counts, values, BUCKET[measure], measured);
  const previous = comparable
    ? bucketValue(previousCounts.counts, previousCounts.values, BUCKET[measure], measured).value
    : null;

  return {
    value,
    count,
    previous,
    basis: comparable ? comparisonBasis(windowKey) : null,
    loading: !pending && (!ready || (comparable && !previousCounts.ready)),
    error: error ?? previousCounts.error,
  };
}
